import { FC, memo, useEffect, useMemo } from "react";
import {
  Analytics,
  Business,
  Chat,
  InsertDriveFile,
  Feedback,
  Home,
  People,
  School,
  Web,
  UploadFile,
  Spa,
  Assignment,
  Campaign,
  Policy,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import { ROLES, User } from "types/user";
import { organizationSelectedSelector, organizationSelector } from "reduxStore/organization/selectors";
import { useAppSelector } from "hooks/useAppSelector";
import { MenuList } from "./MenuList";
import { useAppDispatch } from "hooks/useAppDispatch";
import { SelectOrganizationThunk, setOrganizationsThunk } from "reduxStore/organization/thunks";
import { Organization } from "types/organization";
import {
  useGetGraduationClassesByStudentLazyQuery,
  useGetOrganizationByUserIdLazyQuery,
  useGetOrganizationsLazyQuery,
} from "graphql/__generated__/hooks";
import { useNavigate } from "react-router-dom";
import { OrganizationSelector } from "./OrganizationSelector";
import { useClinicText } from "hooks/useClinicText";
import { AdminRoutes } from "config/routes";
import { useChatUnread } from "context/ChatUnreadContext";

type OrganizationNavProps = { user: User; onClickEvent?: () => void };

interface Option {
  label: string;
  value: Organization;
}

const OrganizationNavComponent: FC<OrganizationNavProps> = ({
  user,
  onClickEvent,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const organization = useAppSelector(organizationSelectedSelector);
  const organizationsFromStore = useAppSelector(organizationSelector);
  const { textYearlyCurriculum, textAcademicYear, textGraduationClass } =
    useClinicText(true);

  const [getOrganizationData, { data: allOrgData }] =
    useGetOrganizationsLazyQuery();
  const [getOrganizationsByUser, { data: orgData }] =
    useGetOrganizationByUserIdLazyQuery();
  const [getDataGradClass, { data: dataGradClass }] =
    useGetGraduationClassesByStudentLazyQuery();

  const organizations = useMemo(() => {
    // Prefer Redux so create/delete on the org list updates the sidebar select
    // without a full page refresh.
    if (
      user?.role === ROLES.ADMIN &&
      organizationsFromStore &&
      organizationsFromStore.length > 0
    ) {
      return organizationsFromStore;
    }
    if (orgData?.organization) {
      if (user?.role !== ROLES.STUDENT && orgData?.organization?.length === 0) {
        navigate("/nodata");
      }
      return orgData.organization as Organization[];
    }
    if (allOrgData?.organization) {
      if (
        allOrgData?.organization?.length === 0 &&
        user?.role !== ROLES.ADMIN
      ) {
        navigate("/nodata");
      }
      return allOrgData.organization as Organization[];
    }
    return undefined;
  }, [allOrgData, orgData, organizationsFromStore, user?.role, navigate]);

  useEffect(() => {
    if (
      dataGradClass?.graduation_class?.length &&
      dataGradClass?.graduation_class?.length > 0 &&
      dataGradClass?.graduation_class[0]?.program?.organization
    ) {
      dispatch(
        SelectOrganizationThunk(
          dataGradClass?.graduation_class[0]?.program?.organization,
        ),
      );
    }
  }, [dataGradClass]);

  useEffect(() => {
    if (user?.role === ROLES.ADMIN) {
      getOrganizationData().then();
    } else if (
      user?.role === ROLES.DIRECTOR ||
      user?.role === ROLES.MANAGER ||
      user?.role === ROLES.COACH
    ) {
      getOrganizationsByUser({ variables: { userId: user?.user_id } }).then();
    } else if (user?.role === ROLES.STUDENT) {
      getDataGradClass({ variables: { userId: user?.user_id } }).then();
    }
  }, [user]);

  useEffect(() => {
    if (user?.role !== ROLES.ADMIN || !allOrgData?.organization) {
      return;
    }
    // Keep Redux in sync with the sidebar fetch so later list mutations
    // (create/delete) can update the select without a full page refresh.
    if (!organizationsFromStore?.length) {
      dispatch(
        setOrganizationsThunk(allOrgData.organization as Organization[]),
      );
    }
  }, [allOrgData, dispatch, organizationsFromStore?.length, user?.role]);

  const options = useMemo<Option[]>(() => {
    if (!organizations) return [];
    if (!organization) {
      dispatch(SelectOrganizationThunk(organizations[0]));
    }
    return organizations?.map((org: Organization) => ({
      label: org.name,
      value: org,
    }));
  }, [organizations]);

  const menuList = useMemo(
    () => [
      {
        title: "Home",
        icon: <Home sx={{ color: "black" }} />,
        path: "/home",
        roles: [ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Organization",
        icon: <Business sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/detail`,
        roles: [
          ROLES.ADMIN,
          ROLES.DIRECTOR,
          ROLES.MANAGER,
          ROLES.COACH,
          ROLES.STUDENT,
        ],
      },
      {
        title: "Chat",
        icon: <Chat sx={{ color: "black" }} />,
        path: AdminRoutes.Chat,
        roles: [ROLES.ADMIN],
      },
      {
        title: "EPA",
        icon: <InsertDriveFile sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/epas`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Competencies",
        icon: <InsertDriveFile sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/competencies`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Domains",
        icon: <InsertDriveFile sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/domains`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "ITER",
        icon: <InsertDriveFile sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/iters`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Surveys",
        icon: <Assignment sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/surveys`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Campaigns",
        icon: <Campaign sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/campaigns`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Users",
        icon: <People sx={{ color: "black" }} />,
        path: "/organizations",
        roles: [],
      },
      {
        title: textYearlyCurriculum,
        icon: <School sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/yearly-curriculum/`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.COACH],
      },
      {
        title: textGraduationClass,
        icon: <School sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/graduation-class/`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.COACH],
      },
      {
        title: "Learners",
        icon: <People sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/learners/`,
        roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR],
      },
      {
        title: "Curriculum Requirements",
        icon: <InsertDriveFile sx={{ color: "black" }} />,
        path: "/organizations",
        roles: [],
      },
      {
        title: "Partner Sites",
        icon: <Web sx={{ color: "black" }} />,
        path: "/organizations",
        roles: [],
      },
      {
        title: "Courses or Services",
        icon: <School sx={{ color: "black" }} />,
        path: "/organizations",
        roles: [],
      },
      {
        title: "Wellness",
        icon: <Spa sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/wellness-resources/`,
        roles: [
          ROLES.ADMIN,
          ROLES.COACH,
          ROLES.STUDENT,
          ROLES.DIRECTOR,
          ROLES.MANAGER,
        ],
      },
      {
        title: "Resources",
        icon: <UploadFile sx={{ color: "black" }} />,
        path: AdminRoutes.ResourcesList,
        roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR],
      },
      {
        title: "Feedback Requests",
        icon: <Feedback sx={{ color: "black" }} />,
        path: "/organizations",
        roles: [],
      },
      {
        title: "Analytics",
        icon: <Analytics sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/analytics/`,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR],
      },
      {
        title: "Metrics",
        icon: <Analytics sx={{ color: "black" }} />,
        path: AdminRoutes.Metrics,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Access log",
        icon: <Policy sx={{ color: "black" }} />,
        path: AdminRoutes.EducationRecordAccessLog,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Feedback",
        icon: <Feedback sx={{ color: "black" }} />,
        path: `/organization/${organization?.org_id}/feedbacks/`,
        roles: [ROLES.ADMIN, ROLES.MANAGER],
      },
    ],
    [textYearlyCurriculum, textAcademicYear, textGraduationClass, organization],
  );

  const { totalUnread } = useChatUnread();

  const organizationMenu = useMemo(() => {
    return menuList
      .filter((item) =>
        user?.role ? item.roles?.includes(user?.role) : false,
      )
      .map((item) =>
        item.path === AdminRoutes.Chat
          ? { ...item, badgeCount: totalUnread }
          : item,
      );
  }, [menuList, user, totalUnread]);

  const shouldShowNav = useMemo(() => {
    return (
      user.role === ROLES.ADMIN ||
      user.role === ROLES.DIRECTOR ||
      user.role === ROLES.MANAGER
    );
  }, [user]);

  const handleSelectOrganization = (selectedOrg: Organization) => {
    dispatch(SelectOrganizationThunk(selectedOrg));
  };

  useEffect(() => {
    if (options.length === 1) {
      dispatch(SelectOrganizationThunk(options[0].value));
    }
  }, [options]);

  return (
    <Box>
      {organization && shouldShowNav && (
        <Box px={1}>
          <OrganizationSelector
            organizations={organizations || []}
            selectedOrganization={organization}
            onSelectOrganization={handleSelectOrganization}
          />
        </Box>
      )}

      {organization && shouldShowNav && (
        <MenuList listItems={organizationMenu} onClickEvent={onClickEvent} />
      )}
    </Box>
  );
};

export const OrganizationNav = memo(OrganizationNavComponent);
